import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, Upload, X, ChefHat, Store, Home, Loader2, Globe } from 'lucide-react';
import { theme } from '../styles/theme';
import {
  iosNativeContent,
  iosNativePageRoot,
  iosNativeScreenTitle,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { analyzeFoodForAcne } from '../utils/foodAcneEngine';
import { getFoodAlternatives } from '../utils/foodAlternatives';
import { analyzeRestaurantMenuItem, type RestaurantMenuAnalysisResult } from '../utils/restaurantAnalyzer';
import type { AlternativeRecipe } from '../data/recipes';
import { createFallbackRecipe } from '../data/recipes';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { listUserScans } from '../services/scanService';
import { useSubscription } from '../hooks/useSubscription';
import { FeatureLock } from '../components/FeatureLock';
import { getFoodScannerDailyLimit } from '../types/subscription';
import { useToast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { analyzeFoodWithChatGPTStrict } from '../services/foodAnalysisServiceStrict';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(
    180deg,
    ${theme.colors.background.secondary} 0%,
    ${theme.colors.background.primary} 100%
  );
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${iosNativeContent}
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: clamp(2rem, 4vw, 2.5rem);
  color: ${theme.colors.text.primary};
  text-align: center;

  ${iosNativeScreenTitle}

  body.native-app & {
    text-align: left;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  text-align: center;
`;

const InputCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const TextInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const PhotoUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 2px dashed ${theme.colors.border.light};
`;

const PhotoPreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: ${theme.borderRadius.md};
  object-fit: contain;
`;

const ResultsCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ScoreDisplay = styled.div<{ $score: number }>`
  text-align: center;
  padding: 2rem;
  border-radius: ${theme.borderRadius['2xl']};
  color: white;
  box-shadow: ${theme.shadows.lg};
  ${props => {
    const score = props.$score;
    if (score <= 30) {
      // Green gradient for low scores
      return `background: linear-gradient(135deg, ${theme.colors.mint[500]} 0%, ${theme.colors.mint[400]} 100%);`;
    } else if (score <= 60) {
      // Yellow/gold gradient for moderate scores
      return `background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);`;
    } else {
      // Red gradient for high scores
      return `background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);`;
    }
  }}
`;

const ScoreValue = styled.div`
  font-size: 4rem;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const ScoreLabel = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  opacity: 0.9;
`;

const Explanation = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const AlternativesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AlternativeCard = styled(Card)`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const AlternativeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 1rem;
`;

const AlternativeName = styled.h4`
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  flex: 1;
`;

const AlternativeDescription = styled.p`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

const RecipeModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const RecipeModalContent = styled(Card)`
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RecipeTitle = styled.h2`
  margin: 0;
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.primary};
`;

const RecipeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RecipeSectionTitle = styled.h3`
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const RecipeList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RecipeListItem = styled.li`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

const RecipeButton = styled(Button)`
  align-self: flex-start;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  background: ${theme.colors.background.secondary};
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
`;

const ModeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  background: ${props => props.$active ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text.secondary};
  
  &:hover {
    background: ${props => props.$active ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  }
`;

const RestaurantInfo = styled.div`
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RestaurantLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const RestaurantName = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const MenuItemName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
`;

const FoodScanPage: React.FC = () => {
  const { userProfile, getCurrentSession } = useAWSAuth();
  const { checkFeature, tier } = useSubscription();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'home' | 'restaurant'>('home');
  const [foodScansToday, setFoodScansToday] = useState(0);
  const [showFoodLimitModal, setShowFoodLimitModal] = useState(false);

  // Load food scan count for today from localStorage
  useEffect(() => {
    if (!userProfile?.uid) return;
    const today = new Date().toDateString();
    const scanData = JSON.parse(localStorage.getItem(`foodScans_${userProfile.uid}`) || '{}');
    if (scanData.date === today) {
      setFoodScansToday(scanData.count || 0);
    } else {
      setFoodScansToday(0);
    }
  }, [userProfile?.uid]);

  // Track food scan when analysis is performed
  const trackFoodScan = () => {
    if (!userProfile?.uid) return;
    const today = new Date().toDateString();
    const scanData = JSON.parse(localStorage.getItem(`foodScans_${userProfile.uid}`) || '{}');
    if (scanData.date === today) {
      scanData.count = (scanData.count || 0) + 1;
    } else {
      scanData.date = today;
      scanData.count = 1;
    }
    localStorage.setItem(`foodScans_${userProfile.uid}`, JSON.stringify(scanData));
    setFoodScansToday(scanData.count);
  };
  const [foodName, setFoodName] = useState('');
  const [ingredientsText, setIngredientsText] = useState('');
  const [foodPhotoFile, setFoodPhotoFile] = useState<File | null>(null);
  const [foodPhotoUrl, setFoodPhotoUrl] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [restaurantAnalysis, setRestaurantAnalysis] = useState<RestaurantMenuAnalysisResult | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<AlternativeRecipe | null>(null);
  const [selectedAlternativeName, setSelectedAlternativeName] = useState<string | null>(null); // Store the alternative name for display
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
  const [latestScanAnalysis, setLatestScanAnalysis] = useState<any>(null); // Store latest scan analysis for personalized recommendations
  const [isSearchingInternet, setIsSearchingInternet] = useState(false); // Track internet search status
  const [isAnalyzingHome, setIsAnalyzingHome] = useState(false); // Loading state for food-at-home API
  
  // Restaurant mode state
  const [restaurantName, setRestaurantName] = useState('');
  const [menuItemName, setMenuItemName] = useState('');
  const [menuItemDescription, setMenuItemDescription] = useState('');

  // Fetch latest scan analysis for personalized restaurant recommendations
  useEffect(() => {
    const fetchLatestScan = async () => {
      if (!userProfile?.uid || !getCurrentSession) {
        return;
      }

      try {
        const session = await getCurrentSession();
        if (!session) {
          return;
        }

        const response = await listUserScans(session, userProfile.uid);
        const completedScans = response.items.filter(s => s.status === 'complete');
        if (completedScans.length > 0) {
          // Sort by timestamp and get the latest
          const sorted = completedScans.sort((a, b) => {
            const timeA = new Date(a.createdAt ?? a.timestamp).getTime();
            const timeB = new Date(b.createdAt ?? b.timestamp).getTime();
            return timeB - timeA;
          });
          setLatestScanAnalysis(sorted[0].analysis);
        }
      } catch (error) {
        console.error('Error fetching latest scan for restaurant recommendations:', error);
      }
    };

    fetchLatestScan();
  }, [userProfile, getCurrentSession]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFoodPhotoFile(file);
      setFoodPhotoUrl(URL.createObjectURL(file));
      // Auto-trigger analysis if food name is already entered
      if (foodName.trim()) {
        setTimeout(() => {
          handleAnalyze();
        }, 100);
      }
    }
  };

  const handleAnalyze = async () => {
    if (mode === 'restaurant') {
      // Restaurant mode analysis
      if (!restaurantName.trim() || !menuItemName.trim()) {
        return;
      }
      
      // Show loading state for internet search
      setIsSearchingInternet(true);

      // CRITICAL: Reset recipe modal state when new analysis is set
      setSelectedRecipe(null);
      setSelectedAlternativeName(null);
      setIsRecipeModalOpen(false);

      try {
        console.log('[FoodScanPage] Starting restaurant menu analysis', {
          restaurantName: restaurantName.trim(),
          menuItemName: menuItemName.trim(),
          tier
        });
        
        const result = await analyzeRestaurantMenuItem({
          restaurantName: restaurantName.trim(),
          menuItemName: menuItemName.trim(),
          menuItemDescription: menuItemDescription.trim() || undefined,
          dietaryPreference: userProfile?.dietaryPreference,
          scanAnalysis: latestScanAnalysis, // Pass latest scan analysis for personalized recommendations
          subscriptionTier: tier, // Pass subscription tier for Pro/Elite users to get more alternatives
        });
        
        console.log('[FoodScanPage] Restaurant analysis complete', {
          hasMenu: result.hasRestaurantMenu,
          menuSource: result.menuSource,
          alternativesCount: result.sameRestaurantAlternatives.length,
          restaurantName: result.restaurantName
        });
        
        setRestaurantAnalysis(result);
        setAnalysis(null); // Clear home mode analysis
        
        // Show success message if menu was found via API
        if (result.menuSource === 'api' && result.sameRestaurantAlternatives.length > 0) {
          showToast(`Found ${result.sameRestaurantAlternatives.length} menu items for ${result.restaurantName}`, 'success');
        } else if (!result.hasRestaurantMenu) {
          showToast('Menu not found in our database. Please check with your server for available options.', 'info');
        }
      } catch (error: any) {
        console.error('[FoodScanPage] Error analyzing restaurant menu item:', error);
        // Show error to user
        const errorMessage = error?.message || 'Failed to analyze menu item. Please try again.';
        showToast(errorMessage, 'error');
        setRestaurantAnalysis(null);
        setAnalysis(null);
      } finally {
        setIsSearchingInternet(false);
      }
    } else {
      // Home mode analysis - uses same OpenAI API (REACT_APP_OPENAI_API_KEY + REACT_APP_API_URL) as GlowBot & cuisine filter
      if (!foodName.trim()) {
        return;
      }

      // Check daily limit for Plus plan
      const dailyLimit = getFoodScannerDailyLimit(tier);
      if (typeof dailyLimit === 'number' && foodScansToday >= dailyLimit) {
        setShowFoodLimitModal(true);
        return;
      }

      setIsAnalyzingHome(true);
      setSelectedRecipe(null);
      setSelectedAlternativeName(null);
      setIsRecipeModalOpen(false);

      try {
        const result = await analyzeFoodWithChatGPTStrict(
          foodName.trim(),
          ingredientsText.trim() || undefined,
          latestScanAnalysis,
          userProfile?.dietaryPreference
        );

        // Add cuisine for fallback recipe compatibility (from local engine)
        const localMeta = analyzeFoodForAcne({ foodName: foodName.trim(), ingredientsText: ingredientsText.trim() || undefined });
        const analysisWithCuisine = { ...result, cuisine: localMeta.cuisine };

        console.log('DEBUG_FOOD_ANALYSIS', {
          foodName: foodName.trim(),
          acneImpactScore: result.acneImpactScore,
          triggers: result.triggers,
          alternatives: result.alternatives?.map((a: any) => a.name) || [],
        });

        setAnalysis(analysisWithCuisine);
        setRestaurantAnalysis(null);
        trackFoodScan();
      } catch (err) {
        console.error('[FoodScanPage] Home analysis error:', err);
        showToast('Analysis failed. Please try again.', 'error');
        // Fallback to local analysis so it still works
        const result = analyzeFoodForAcne({
          foodName: foodName.trim(),
          ingredientsText: ingredientsText.trim() || undefined,
        });
        const { alternatives, brands } = getFoodAlternatives({
          foodName: foodName.trim(),
          cuisine: result.cuisine,
          tags: result.tags,
          acneImpactCategory: result.acneImpactCategory,
          acneImpactScore: result.acneImpactScore,
          dietaryPreference: userProfile?.dietaryPreference,
        });
        setAnalysis({ ...result, alternatives, brands });
        setRestaurantAnalysis(null);
        trackFoodScan();
      } finally {
        setIsAnalyzingHome(false);
      }
    }
  };

  // CRITICAL FIX: Explicitly store the recipe for the clicked card
  // This ensures the modal always shows the recipe for the card that was clicked
  // Each alternative card passes its own unique recipe object to this handler
  const handleViewRecipe = (recipe: AlternativeRecipe, alternativeName: string) => {
    // Directly set the recipe - React will handle the state update
    // The recipe object is passed directly from the clicked card, ensuring correctness
    // Also store the alternative name so we can display it exactly as shown in the card
    setSelectedRecipe(recipe);
    setSelectedAlternativeName(alternativeName);
    setIsRecipeModalOpen(true);
  };

  const handleCloseRecipe = () => {
    setIsRecipeModalOpen(false);
    // Clear the selected recipe when closing to prevent "bleed over" to next card
    setSelectedRecipe(null);
    setSelectedAlternativeName(null);
  };

  const getCategoryColor = (category: 'low' | 'moderate' | 'high') => {
    switch (category) {
      case 'low':
        return theme.colors.status.success;
      case 'moderate':
        return theme.colors.status.warning;
      case 'high':
        return theme.colors.status.error;
      default:
        return theme.colors.text.secondary;
    }
  };

  const getCategoryLabel = (category: 'low' | 'moderate' | 'high') => {
    switch (category) {
      case 'low':
        return 'Skin-friendly';
      case 'moderate':
        return 'Okay sometimes';
      case 'high':
        return 'Acne-triggering';
      default:
        return category;
    }
  };


  const dailyLimit = getFoodScannerDailyLimit(tier);
  const canScan = dailyLimit === 'unlimited' || (typeof dailyLimit === 'number' && foodScansToday < dailyLimit);

  return (
    <PageContainer>
      <Container>
        <div>
          <Title>Food Scanner</Title>
          <Subtitle>
            See how different foods might affect your skin and discover healthier alternatives
          </Subtitle>
        </div>

        {!canScan && typeof dailyLimit === 'number' && (
          <Card padding="lg" style={{ marginBottom: '1.5rem', background: theme.colors.accent.peach + '20', border: `1px solid ${theme.colors.accent.peach}` }}>
            <p style={{ margin: 0, color: theme.colors.text.primary }}>
              You've reached your daily limit of {dailyLimit} food scans. Upgrade to Pro for unlimited scans.
            </p>
          </Card>
        )}

        <ModeToggle>
          <ModeButton
            $active={mode === 'home'}
            onClick={() => {
              if (!checkFeature('foodScannerAtHome')) {
                navigate('/pricing');
                return;
              }
              setMode('home');
              setAnalysis(null);
              setRestaurantAnalysis(null);
              setRestaurantName('');
              setMenuItemName('');
              setMenuItemDescription('');
              setSelectedRecipe(null);
              setSelectedAlternativeName(null);
              setIsRecipeModalOpen(false);
            }}
            disabled={!canScan && typeof dailyLimit === 'number'}
            style={{
              opacity: checkFeature('foodScannerAtHome') && canScan ? 1 : 0.6,
              cursor: checkFeature('foodScannerAtHome') && canScan ? 'pointer' : 'not-allowed',
            }}
          >
            <Home size={18} />
            Food at home
            {!checkFeature('foodScannerAtHome') && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>🔒</span>
            )}
          </ModeButton>
          {/* Restaurant mode only available for Pro+ */}
          {checkFeature('restaurantDatabaseAccess') ? (
            <ModeButton
              $active={mode === 'restaurant'}
              onClick={() => {
                setMode('restaurant');
                setAnalysis(null);
                setRestaurantAnalysis(null);
                setFoodName('');
                setIngredientsText('');
                setFoodPhotoFile(null);
                setFoodPhotoUrl(null);
                setSelectedRecipe(null);
                setSelectedAlternativeName(null);
                setIsRecipeModalOpen(false);
              }}
            >
              <Store size={18} />
              Food at a restaurant
            </ModeButton>
          ) : null}
        </ModeToggle>

        <InputCard>
          {mode === 'restaurant' ? (
            !checkFeature('restaurantDatabaseAccess') ? (
              <FeatureLock
                feature="Restaurant Database Access"
                requiredTier="pro"
                message="Access to our database of 50+ restaurant menus and grocery food scanning is available in GlowLens Pro."
              />
            ) : (
            <>
              <InputGroup>
                <Label htmlFor="restaurantName">Restaurant name (Worldwide)</Label>
                <TextInput
                  id="restaurantName"
                  type="text"
                  placeholder="Chipotle, McDonald's, Nando's, Domino's, KFC..."
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="menuItemName">What are you thinking of ordering?</Label>
                <TextInput
                  id="menuItemName"
                  type="text"
                  placeholder="Orange chicken bowl, fried rice, chicken alfredo, burger with fries..."
                  value={menuItemName}
                  onChange={(e) => setMenuItemName(e.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="menuItemDescription">Any details/ingredients from the menu description? (optional)</Label>
                <TextArea
                  id="menuItemDescription"
                  placeholder="Crispy fried chicken in sweet orange glaze with rice and veggies..."
                  value={menuItemDescription}
                  onChange={(e) => setMenuItemDescription(e.target.value)}
                />
              </InputGroup>
            </>
            )
          ) : (
            <>
              <InputGroup>
                <Label htmlFor="foodName">What are you about to eat?</Label>
                <TextInput
                  id="foodName"
                  type="text"
                  placeholder="Ex: vanilla ice cream, spicy ramen, chocolate chip cookies..."
                  value={foodName}
                  onChange={(e) => setFoodName(e.target.value)}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="ingredients">Ingredients (optional)</Label>
                <TextArea
                  id="ingredients"
                  placeholder="List any ingredients you know about..."
                  value={ingredientsText}
                  onChange={(e) => setIngredientsText(e.target.value)}
                />
              </InputGroup>
            </>
          )}

          {mode === 'home' && (
            <PhotoUploadSection>
              <Label>Upload food photo (optional)</Label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
                id="photoUpload"
              />
              <label htmlFor="photoUpload">
                <Button
                  variant="outline"
                  as="span"
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <Upload size={18} />
                  Choose Photo
                </Button>
              </label>
              {foodPhotoUrl && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <PhotoPreview src={foodPhotoUrl} alt="Food preview" />
                  <button
                    onClick={() => {
                      setFoodPhotoFile(null);
                      setFoodPhotoUrl(null);
                    }}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              {foodPhotoUrl && (
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
                  We'll use this photo for label/ingredient recognition in the future. For now, you can still type the food name and ingredients.
                </p>
              )}
            </PhotoUploadSection>
          )}

          <Button
            variant="primary"
            onClick={handleAnalyze}
            disabled={
              mode === 'restaurant' 
                ? (!restaurantName.trim() || !menuItemName.trim() || isSearchingInternet) 
                : (!foodName.trim() || isAnalyzingHome || (!canScan && typeof dailyLimit === 'number'))
            }
            style={{ width: '100%' }}
          >
            {isSearchingInternet ? (
              <>
                <Loader2 size={18} style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                Searching for menu online...
              </>
            ) : isAnalyzingHome ? (
              <>
                <Loader2 size={18} style={{ marginRight: '0.5rem', animation: 'spin 1s linear infinite' }} />
                Analyzing food...
              </>
            ) : (
              <>
                <Search size={18} style={{ marginRight: '0.5rem' }} />
                {mode === 'restaurant' ? 'Analyze menu item for acne' : 'Analyze food for acne'}
              </>
            )}
          </Button>
          
          {isSearchingInternet && (
            <div style={{
              padding: '1rem',
              background: theme.colors.background.secondary,
              borderRadius: theme.borderRadius.md,
              marginTop: '1rem',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}>
              <Globe size={16} color={theme.colors.primary[500]} />
              <p style={{
                margin: 0,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.text.secondary,
              }}>
                Searching the internet for {restaurantName}'s menu...
              </p>
            </div>
          )}
        </InputCard>

        {mode === 'restaurant' && restaurantAnalysis && (
          <ResultsCard>
            <RestaurantInfo>
              <RestaurantLabel>Restaurant</RestaurantLabel>
              <RestaurantName>{restaurantAnalysis.restaurantName}</RestaurantName>
              <MenuItemName>{restaurantAnalysis.menuItemName}</MenuItemName>
            </RestaurantInfo>

            <ScoreDisplay $score={restaurantAnalysis.acneImpactScore}>
              <ScoreValue>{restaurantAnalysis.acneImpactScore}</ScoreValue>
              <ScoreLabel>Acne Impact Score</ScoreLabel>
              <Badge
                variant={restaurantAnalysis.acneImpactCategory === 'low' ? 'success' : restaurantAnalysis.acneImpactCategory === 'moderate' ? 'warning' : 'destructive'}
                size="md"
                style={{ marginTop: '1rem', alignSelf: 'center' }}
              >
                {getCategoryLabel(restaurantAnalysis.acneImpactCategory)}
              </Badge>
            </ScoreDisplay>

            <Explanation>{restaurantAnalysis.explanation}</Explanation>

            {restaurantAnalysis.triggers.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                  Potential triggers:
                </h4>
                <TagsContainer>
                  {restaurantAnalysis.triggers.map((trigger: string, index: number) => (
                    <Badge key={index} variant="info" size="sm">
                      {trigger}
                    </Badge>
                  ))}
                </TagsContainer>
              </div>
            )}

            {restaurantAnalysis.protectiveFactors.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                  Protective factors:
                </h4>
                <TagsContainer>
                  {restaurantAnalysis.protectiveFactors.map((factor: string, index: number) => (
                    <Badge key={index} variant="success" size="sm">
                      {factor}
                    </Badge>
                  ))}
                </TagsContainer>
              </div>
            )}

            {/* Show message if we don't have the restaurant's menu */}
            {!restaurantAnalysis.hasRestaurantMenu && restaurantAnalysis.sameRestaurantAlternatives.length === 0 && (
              <div style={{
                padding: '1.5rem',
                background: theme.colors.background.secondary,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border.light}`,
                marginBottom: '1rem',
                textAlign: 'center',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.base,
                  color: theme.colors.text.primary,
                  lineHeight: 1.6,
                  fontWeight: theme.typography.fontWeight.medium,
                }}>
                  This restaurant's menu items cannot be found.
                </p>
                <p style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.text.secondary,
                  lineHeight: 1.5,
                }}>
                  We don't have the menu for {restaurantAnalysis.restaurantName} in our database yet.
                  {restaurantAnalysis.menuSource === 'api' && (
                    <span style={{ display: 'block', marginTop: '0.5rem', fontStyle: 'italic', color: theme.colors.primary[500] }}>
                      <Globe size={14} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
                      Menu found via internet search - please verify with your server
                    </span>
                  )}
                  {restaurantAnalysis.menuSource !== 'api' && (
                    <span> Please check with your server for skin-friendly options.</span>
                  )}
                </p>
              </div>
            )}

            {/* Show success message when menu items are found via API */}
            {restaurantAnalysis.menuSource === 'api' && restaurantAnalysis.sameRestaurantAlternatives.length > 0 && (
              <div style={{
                padding: '1rem',
                background: theme.colors.primary[50],
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.primary[200]}`,
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <Globe size={16} color={theme.colors.primary[600]} />
                <p style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.primary[700],
                }}>
                  <strong>Menu found via internet search:</strong> Found {restaurantAnalysis.sameRestaurantAlternatives.length} menu items for {restaurantAnalysis.restaurantName}. Please verify availability with your server.
                </p>
              </div>
            )}

            {restaurantAnalysis.sameRestaurantAlternatives.length > 0 && (
              <AlternativesSection>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold }}>
                  Better picks at {restaurantAnalysis.restaurantName} your skin may prefer
                  {restaurantAnalysis.menuSource === 'api' && (
                    <span style={{ 
                      fontSize: theme.typography.fontSize.sm, 
                      fontWeight: theme.typography.fontWeight.normal,
                      color: theme.colors.text.secondary,
                      marginLeft: '0.5rem'
                    }}>
                      ({restaurantAnalysis.sameRestaurantAlternatives.length} items found)
                    </span>
                  )}
                </h3>
                {/* Note: Template menus are no longer used - all menus are 100% accurate */}
                {restaurantAnalysis.sameRestaurantAlternatives.map((alt, index) => (
                  <AlternativeCard key={`${alt.name}-${index}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.75rem' }}>
                      <div style={{ flex: 1 }}>
                        <AlternativeName>{alt.name}</AlternativeName>
                        <Badge
                          variant={alt.acneImpactScore <= 35 ? 'success' : alt.acneImpactScore <= 65 ? 'warning' : 'destructive'}
                          size="sm"
                          style={{ marginTop: '0.25rem', display: 'inline-block' }}
                        >
                          Score: {alt.acneImpactScore}
                        </Badge>
                      </div>
                    </div>
                    <AlternativeDescription>{alt.explanation}</AlternativeDescription>
                  </AlternativeCard>
                ))}
              </AlternativesSection>
            )}

            {restaurantAnalysis.isRecommended && restaurantAnalysis.sameRestaurantAlternatives.length > 0 && (
              <div style={{ textAlign: 'center', padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                <p style={{ margin: 0, color: theme.colors.status.success, fontWeight: theme.typography.fontWeight.medium }}>
                  This is a relatively skin-friendly choice. If you'd like to upgrade it even more, consider the suggestions above.
                </p>
              </div>
            )}
          </ResultsCard>
        )}

        {mode === 'home' && analysis && (
          <ResultsCard>
            <ScoreDisplay $score={analysis.acneImpactScore}>
              <ScoreValue>{analysis.acneImpactScore}</ScoreValue>
              <ScoreLabel>Acne Impact Score</ScoreLabel>
              <Badge
                variant={analysis.acneImpactCategory === 'low' ? 'success' : analysis.acneImpactCategory === 'moderate' ? 'warning' : 'destructive'}
                size="md"
                style={{ marginTop: '1rem', alignSelf: 'center' }}
              >
                {getCategoryLabel(analysis.acneImpactCategory)}
              </Badge>
            </ScoreDisplay>

            <Explanation>{analysis.explanation}</Explanation>

            {analysis.triggers.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                  Potential triggers:
                </h4>
                <TagsContainer>
                  {analysis.triggers.map((trigger: string, index: number) => (
                    <Badge key={index} variant="info" size="sm">
                      {trigger}
                    </Badge>
                  ))}
                </TagsContainer>
              </div>
            )}

            {analysis.protectiveFactors.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold }}>
                  Protective factors:
                </h4>
                <TagsContainer>
                  {analysis.protectiveFactors.map((factor: string, index: number) => (
                    <Badge key={index} variant="success" size="sm">
                      {factor}
                    </Badge>
                  ))}
                </TagsContainer>
              </div>
            )}

            {analysis.alternatives && analysis.alternatives.length > 0 && (
              <AlternativesSection>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold }}>
                  Healthier alternatives your skin may prefer
                </h3>
                {analysis.alternatives.map((alt: any, index: number) => {
                  // Use attached recipe or create fallback so every food has View Recipe button
                  const recipe: AlternativeRecipe = alt.recipe || createFallbackRecipe(alt.name, alt.description, analysis.cuisine);
                  
                  // Each card has its own unique recipe object bound to this specific alternative
                  // Use the alternative's unique ID as the key to ensure React tracks it correctly
                  // Pass the exact recipe object from THIS card to the handler
                  return (
                    <AlternativeCard key={alt.id || `alt-${alt.name}-${index}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.75rem' }}>
                        <div style={{ flex: 1 }}>
                          <AlternativeName>{alt.name}</AlternativeName>
                          <Badge
                            variant={alt.acneImpactScore <= 35 ? 'success' : alt.acneImpactScore <= 65 ? 'warning' : 'destructive'}
                            size="sm"
                            style={{ marginTop: '0.25rem', display: 'inline-block' }}
                          >
                            Score: {alt.acneImpactScore}
                          </Badge>
                        </div>
                      </div>
                      <AlternativeDescription>{alt.description}</AlternativeDescription>
                      <RecipeButton
                        variant="primary"
                        size="sm"
                        onClick={() => handleViewRecipe(recipe, alt.name)}
                        style={{ alignSelf: 'flex-start', marginTop: '0.25rem' }}
                      >
                        <ChefHat size={16} style={{ marginRight: '0.5rem' }} />
                        View recipe
                      </RecipeButton>
                    </AlternativeCard>
                  );
                })}
              </AlternativesSection>
            )}

            {analysis.brands && analysis.brands.length > 0 && (
              <AlternativesSection style={{ marginTop: '2rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.semibold }}>
                  Healthy snack brands to try
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {analysis.brands.map((brand: any, brandIndex: number) => (
                    <div 
                      key={brandIndex}
                      style={{
                        padding: '1rem',
                        background: theme.colors.background.secondary,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontSize: theme.typography.fontSize.base, 
                            fontWeight: theme.typography.fontWeight.semibold,
                            color: theme.colors.text.primary,
                            marginBottom: '0.5rem'
                          }}>
                            {brand.name}
                          </div>
                          <Badge
                            variant={brand.acneImpactScore <= 35 ? 'success' : brand.acneImpactScore <= 65 ? 'warning' : 'destructive'}
                            size="sm"
                            style={{ display: 'inline-block' }}
                          >
                            Score: {brand.acneImpactScore}
                          </Badge>
                        </div>
                      </div>
                      <p style={{ 
                        margin: '0.5rem 0 0.75rem 0', 
                        fontSize: theme.typography.fontSize.sm, 
                        color: theme.colors.text.secondary,
                        lineHeight: 1.6
                      }}>
                        {brand.description}
                      </p>
                      <div style={{
                        padding: '0.75rem',
                        background: theme.colors.background.primary,
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.border}`
                      }}>
                        <div style={{ 
                          fontSize: theme.typography.fontSize.sm, 
                          fontWeight: theme.typography.fontWeight.semibold,
                          color: theme.colors.text.primary,
                          marginBottom: '0.5rem'
                        }}>
                          Why it's better for your skin:
                        </div>
                        <p style={{ 
                          margin: 0, 
                          fontSize: theme.typography.fontSize.sm, 
                          color: theme.colors.text.secondary,
                          lineHeight: 1.6
                        }}>
                          {brand.whyHealthy}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </AlternativesSection>
            )}

            {analysis.acneImpactCategory === 'low' && (
              <div style={{ textAlign: 'center', padding: '1rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                <p style={{ margin: 0, color: theme.colors.status.success, fontWeight: theme.typography.fontWeight.medium }}>
                  Nice choice — low acne impact 🎉
                </p>
              </div>
            )}
          </ResultsCard>
        )}
      </Container>

      {/* CRITICAL FIX: Modal only renders when BOTH conditions are true:
          1. Modal is open
          2. A specific recipe is selected
          This prevents showing stale or wrong recipes */}
      {isRecipeModalOpen && selectedRecipe && (
        <RecipeModal $isOpen={isRecipeModalOpen} onClick={handleCloseRecipe}>
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <RecipeModalContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                {/* CRITICAL: Use the alternative name for the title to match exactly what the user clicked
                    This ensures "Zucchini bread with whole grains" shows as "Zucchini bread with whole grains"
                    not "Zucchini Bread with Whole Grains" */}
                <RecipeTitle>{selectedAlternativeName || selectedRecipe.title}</RecipeTitle>
                <button
                  onClick={handleCloseRecipe}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X size={20} color={theme.colors.text.secondary} />
                </button>
              </div>
              <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                {selectedRecipe.cuisine} • {selectedRecipe.servings} servings • {selectedRecipe.caloriesPerServing} calories per serving
              </p>

              <RecipeSection>
                <RecipeSectionTitle>Ingredients</RecipeSectionTitle>
                <RecipeList>
                  {selectedRecipe.ingredients.map((ing, idx) => (
                    <RecipeListItem key={idx}>
                      {ing.amount} {ing.unit} {ing.name}
                      {ing.notes && ` (${ing.notes})`}
                    </RecipeListItem>
                  ))}
                </RecipeList>
              </RecipeSection>

              <RecipeSection>
                <RecipeSectionTitle>Instructions</RecipeSectionTitle>
                <RecipeList style={{ listStyle: 'decimal' }}>
                  {selectedRecipe.steps.map((step, idx) => (
                    <RecipeListItem key={idx}>{step}</RecipeListItem>
                  ))}
                </RecipeList>
              </RecipeSection>

              {selectedRecipe.notes && (
                <RecipeSection>
                  <RecipeSectionTitle>Notes</RecipeSectionTitle>
                  <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
                    {selectedRecipe.notes}
                  </p>
                </RecipeSection>
              )}
            </RecipeModalContent>
          </div>
        </RecipeModal>
      )}

      {/* Food Scanner Daily Limit Reached Modal */}
      {showFoodLimitModal && (() => {
        const dailyLimit = getFoodScannerDailyLimit(tier);
        return (
          <RecipeModal $isOpen={true} onClick={() => setShowFoodLimitModal(false)}>
            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              <RecipeModalContent>
              <RecipeTitle>Daily Food Scan Limit Reached</RecipeTitle>
              <p style={{ margin: 0, color: theme.colors.text.secondary, lineHeight: 1.6 }}>
                You've reached your daily limit of {typeof dailyLimit === 'number' ? dailyLimit : 'unlimited'} food scans for today.
                {tier === 'plus' ? (
                  <span style={{ display: 'block', marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm }}>
                    Upgrade to Pro for unlimited food scans.
                  </span>
                ) : (
                  <span style={{ display: 'block', marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm }}>
                    Upgrade your plan to access unlimited food scans.
                  </span>
                )}
              </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outline"
                    onClick={() => setShowFoodLimitModal(false)}
                  >
                    Maybe Later
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowFoodLimitModal(false);
                      navigate('/pricing');
                    }}
                  >
                    Upgrade
                  </Button>
                </div>
              </RecipeModalContent>
            </div>
          </RecipeModal>
        );
      })()}
    </PageContainer>
  );
};

export default FoodScanPage;
